import { useState, type JSX } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Check, Copy, Gift } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@caratuva/ui';
import type { AffiliateReferral, AffiliateSummary } from '../lib/api';
import { getAffiliateSummary } from '../lib/api';

/*
 * Affiliate referral program. Every org has a stable referral code + link
 * (?ref=<code> on /login); when a referred org signs up, this org earns a
 * share of GetPaid's fee on that org's volume, at a rate that increases with
 * tier (mtd referred volume). Earnings only pay out once the org has a Pix
 * payout wallet provisioned for the active mode (`payoutReady`) — until then
 * we nudge them to finish onboarding.
 */

export const Route = createFileRoute('/_app/affiliate')({
  component: AffiliatePage,
});

const bpsFormatter = new Intl.NumberFormat(undefined, {
  style: 'percent',
  maximumFractionDigits: 2,
});

function formatBps(bps: number): string {
  return bpsFormatter.format(bps / 10000);
}

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function formatUsd(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return usdFormatter.format(n);
}

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive';

const REFERRAL_STATUS_VARIANT: Record<string, BadgeVariant> = {
  pending: 'outline',
  active: 'default',
  churned: 'destructive',
};

function referralStatusVariant(status: string): BadgeVariant {
  return REFERRAL_STATUS_VARIANT[status] ?? 'secondary';
}

function AffiliatePage(): JSX.Element {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['affiliate-summary'],
    queryFn: ({ signal }) => getAffiliateSummary(signal),
  });

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'affiliate.title', defaultMessage: 'Affiliate' })}
        </h1>
        <p className="mt-1 text-sm text-navy-600">
          {intl.formatMessage({
            id: 'affiliate.subtitle',
            defaultMessage:
              'Share your referral link. When an organization you refer signs up and starts collecting, you earn a share of the fee GetPaid charges them.',
          })}
        </p>
      </div>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'affiliate.loading',
            defaultMessage: 'Loading affiliate summary…',
          })}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-danger">
          {intl.formatMessage({
            id: 'affiliate.error',
            defaultMessage: 'Failed to load affiliate summary.',
          })}
        </p>
      ) : (
        <AffiliateSummaryView data={query.data} />
      )}
    </section>
  );
}

function ReferralLinkField({ value }: { value: string }): JSX.Element {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  const label = intl.formatMessage({
    id: 'affiliate.referralLink.label',
    defaultMessage: 'Your referral link',
  });

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(
        intl.formatMessage({
          id: 'affiliate.referralLink.copiedToast',
          defaultMessage: 'Referral link copied to clipboard.',
        }),
      );
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(
        intl.formatMessage({
          id: 'affiliate.referralLink.copyFailedToast',
          defaultMessage: 'Could not copy the link. Please copy it manually.',
        }),
      );
    }
  }

  return (
    <div>
      <label
        htmlFor="affiliate-referral-link"
        className="text-xs uppercase tracking-wide text-navy-500"
      >
        {label}
      </label>
      <div className="mt-1 flex items-stretch gap-2">
        <input
          id="affiliate-referral-link"
          readOnly
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 rounded-md border border-navy-200 bg-white px-3 py-2 text-sm text-navy-900"
        />
        <Button type="button" variant="outline" onClick={handleCopy}>
          {copied ? (
            <>
              <Check aria-hidden className="h-4 w-4" />
              {intl.formatMessage({
                id: 'affiliate.referralLink.copied',
                defaultMessage: 'Copied!',
              })}
            </>
          ) : (
            <>
              <Copy aria-hidden className="h-4 w-4" />
              {intl.formatMessage({ id: 'affiliate.referralLink.copy', defaultMessage: 'Copy' })}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function AffiliateSummaryView({ data }: { data: AffiliateSummary }): JSX.Element {
  const intl = useIntl();

  return (
    <div className="space-y-6">
      <ReferralLinkField value={data.referralUrl} />
      <p className="text-sm text-navy-600">
        {intl.formatMessage({
          id: 'affiliate.shareNudge',
          defaultMessage:
            'Anyone who signs up with your link gets 50% off our fee for their first month — and you earn a share of our fee for their first 12 months.',
        })}
      </p>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <StatCard
          label={intl.formatMessage({
            id: 'affiliate.stat.currentShare',
            defaultMessage: 'Current share',
          })}
          value={formatBps(data.currentShareBps)}
        />
        <StatCard
          label={intl.formatMessage({
            id: 'affiliate.stat.mtdVolume',
            defaultMessage: 'MTD referred volume',
          })}
          value={formatUsd(data.mtdReferredVolumeUsd)}
        />
        <StatCard
          label={intl.formatMessage({
            id: 'affiliate.stat.nextTier',
            defaultMessage: 'Next tier',
          })}
          value={
            data.nextTier
              ? intl.formatMessage(
                  {
                    id: 'affiliate.stat.nextTier.value',
                    defaultMessage: 'At {volume} → {share}',
                  },
                  {
                    volume: formatUsd(data.nextTier.minVolumeUsd),
                    share: formatBps(data.nextTier.shareBps),
                  },
                )
              : intl.formatMessage({
                  id: 'affiliate.stat.nextTier.topTier',
                  defaultMessage: 'You’re at the top tier',
                })
          }
        />
        <StatCard
          label={intl.formatMessage({
            id: 'affiliate.stat.settledEarnings',
            defaultMessage: 'Settled earnings',
          })}
          value={formatUsd(data.earnings.settledTotal)}
        />
        <StatCard
          label={intl.formatMessage({
            id: 'affiliate.stat.pendingEarnings',
            defaultMessage: 'Pending earnings',
          })}
          value={formatUsd(data.earnings.pendingTotal)}
        />
      </div>

      {!data.payoutReady ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex flex-col gap-2 p-4">
            <p className="text-sm font-medium text-amber-900">
              {intl.formatMessage({
                id: 'affiliate.payoutNotReady.title',
                defaultMessage: 'Add your Pix payout to receive affiliate earnings',
              })}
            </p>
            <p className="text-sm text-amber-800">
              {intl.formatMessage({
                id: 'affiliate.payoutNotReady.description',
                defaultMessage:
                  "You've earned affiliate fees, but we need a Pix payout destination before we can pay you out.",
              })}
            </p>
            <Link
              to="/onboarding"
              className="text-sm font-medium text-amber-900 underline underline-offset-2"
            >
              {intl.formatMessage({
                id: 'affiliate.payoutNotReady.cta',
                defaultMessage: 'Add your Pix payout in onboarding to receive affiliate earnings',
              })}
            </Link>
          </CardContent>
        </Card>
      ) : null}

      <div>
        <h2 className="mb-2 text-base font-semibold text-navy-900">
          {intl.formatMessage({ id: 'affiliate.referrals.title', defaultMessage: 'Referrals' })}
        </h2>
        {data.referrals.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-navy-200 bg-white px-6 py-10 text-center">
            <Gift aria-hidden className="h-6 w-6 text-navy-500" strokeWidth={1.5} />
            <p className="text-sm text-navy-600">
              {intl.formatMessage({
                id: 'affiliate.referrals.empty',
                defaultMessage: 'No referrals yet. Share your link to start earning.',
              })}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-navy-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {intl.formatMessage({
                      id: 'affiliate.referrals.column.name',
                      defaultMessage: 'Organization',
                    })}
                  </TableHead>
                  <TableHead>
                    {intl.formatMessage({
                      id: 'affiliate.referrals.column.createdAt',
                      defaultMessage: 'Referred',
                    })}
                  </TableHead>
                  <TableHead>
                    {intl.formatMessage({
                      id: 'affiliate.referrals.column.status',
                      defaultMessage: 'Status',
                    })}
                  </TableHead>
                  <TableHead>
                    {intl.formatMessage({
                      id: 'affiliate.referrals.column.commission',
                      defaultMessage: 'Commission',
                    })}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.referrals.map((referral: AffiliateReferral) => (
                  <TableRow key={referral.orgId}>
                    <TableCell className="font-medium text-navy-900">
                      {referral.name ?? `${referral.orgId.slice(0, 8)}…`}
                    </TableCell>
                    <TableCell className="text-navy-600 tabular-nums">
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={referralStatusVariant(referral.status)}>
                        {intl.formatMessage({
                          id: `affiliate.referrals.status.${referral.status}`,
                          defaultMessage: referral.status,
                        })}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-navy-600">
                      <CommissionTag referral={referral} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

// Renders only when the backend has started sending the (optional) 12-month
// commission-window fields on a referral row — rows without them show
// nothing rather than a misleading blank state.
function CommissionTag({ referral }: { referral: AffiliateReferral }): JSX.Element | null {
  const intl = useIntl();
  if (referral.commissionEndsAt) {
    return (
      <span className="text-xs text-navy-500">
        {intl.formatMessage(
          {
            id: 'affiliate.referrals.commission.earningUntil',
            defaultMessage: 'Earning until {date}',
          },
          { date: new Date(referral.commissionEndsAt).toLocaleDateString() },
        )}
      </span>
    );
  }
  if (referral.commissionActive === false) {
    return (
      <span className="text-xs text-navy-400">
        {intl.formatMessage({
          id: 'affiliate.referrals.commission.ended',
          defaultMessage: 'Commission period ended',
        })}
      </span>
    );
  }
  return null;
}

function StatCard({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-navy-500">{label}</p>
        <p className="mt-1 text-lg font-semibold text-navy-900">{value}</p>
      </CardContent>
    </Card>
  );
}
